import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { validateIndicesParams } from '@utils/indicesParamValidation';
import { appendIndicesTabParam } from '@utils/queryParams';
import { notify } from '@utils/notify';

/**
 * Requires an Indices token and silently replaces missing/invalid tabs with
 * World before the page mounts. Analysis and instrument context are separate.
 */
export const useEnsureIndicesParams = (): boolean => {
  const location = useLocation();
  const navigate = useNavigate();
  const validation = useMemo(
    () => validateIndicesParams(location.search),
    [location.search],
  );
  const issueKey = validation.issues.map((issue) => issue.reason).join('|');
  const primaryIssue = validation.issues[0];

  useEffect(() => {
    if (!primaryIssue) return;

    notify({ level: 'error', message: primaryIssue.message });
  }, [issueKey, primaryIssue]);

  useEffect(() => {
    if (!validation.isValid || !validation.needsTabNormalization) return;

    navigate(
      {
        pathname: location.pathname,
        search: appendIndicesTabParam(location.search, validation.tab),
        hash: location.hash,
      },
      { replace: true, state: location.state },
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    validation.isValid,
    validation.needsTabNormalization,
    validation.tab,
  ]);

  return validation.isValid && !validation.needsTabNormalization;
};
